import { Fragment, useState } from 'react'
import {
  PlusIcon,
  XMarkIcon,
  ChevronRightIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button, Spinner } from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import { useCreateCloseCalendarRule } from './api'
import type { ConditionItem } from './api'
import {
  buildCloseCalendarPipelineSteps,
  humanizeCloseCalendarKey,
  type CloseCalendarPipelineStep,
} from './utils'
import type { CloseCalendarPeriod, CloseCalendarRule, CloseCalendarTemplate } from './types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RuleEditState = {
  enforcement_mode: 'hard' | 'soft'
  condition_logic: 'all' | 'any'
  is_active: boolean
  allow_override: boolean
  override_requires_reason: boolean
  override_requires_dual_approval: boolean
}

type ConditionDraft = {
  id: string
  type: 'applet' | 'approval'
  key: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const APPROVAL_ROLES: { key: string; label: string }[] = [
  { key: 'controller', label: 'Controller' },
  { key: 'cfo', label: 'CFO' },
  { key: 'director_of_accounting', label: 'Director of Accounting' },
  { key: 'manager', label: 'Manager' },
  { key: 'staff_accountant', label: 'Staff Accountant' },
  { key: 'vp_finance', label: 'VP Finance' },
]

const ACTION_READABLE: Record<string, string> = {
  retrieve_data: 'data retrieval',
  run_agent: 'agent run',
  release_downstream: 'downstream release',
  publish_output: 'output publishing',
  update_decisions: 'decision updates',
}

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red/50 focus-visible:ring-offset-1'

const FORM_SELECT =
  'h-8 w-full rounded-md border border-gray-200 bg-white px-2.5 text-xs text-charcoal ' +
  'focus:border-eliza-red/60 focus:outline-none focus:ring-2 focus:ring-eliza-red/10 ' +
  'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'

function stepName(key: string) {
  return humanizeCloseCalendarKey(key)
}

function stepLabel(key: string, steps: CloseCalendarPipelineStep[]) {
  return steps.find(step => step.key === key)?.label ?? stepName(key)
}

function roleName(key: string) {
  return APPROVAL_ROLES.find(r => r.key === key)?.label ?? key.replace(/_/g, ' ')
}

function pipelinePosition(targetKey: string, steps: CloseCalendarPipelineStep[]): number {
  const index = steps.findIndex(step => step.key === targetKey)
  return index >= 0 ? index : steps.length
}

// ---------------------------------------------------------------------------
// Pipeline strip
// ---------------------------------------------------------------------------

function PipelineStrip({
  ruleGroups,
  steps,
}: {
  ruleGroups: CloseCalendarRule[][]
  steps: CloseCalendarPipelineStep[]
}) {
  const gatedSteps = new Set(
    ruleGroups
      .filter(group => group.some(rule => rule.is_active))
      .map(group => group[0].target_item_key),
  )

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 px-5 py-4 dark:border-gray-700/50 dark:bg-white/[0.02]">
      <p className="mb-4 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
        Close pipeline
      </p>
      <div className="flex items-start overflow-x-auto pb-1">
        {steps.map((step, i) => {
          const hasRule = gatedSteps.has(step.key)
          return (
            <Fragment key={step.key}>
              {i > 0 && (
                <div className="mt-3 flex min-w-[24px] flex-1 items-center">
                  <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
                  <ChevronRightIcon className="h-3 w-3 shrink-0 text-gray-300 dark:text-gray-600" />
                </div>
              )}
              <div className="flex w-[72px] shrink-0 flex-col items-center gap-1.5">
                <div className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold tabular-nums',
                  hasRule
                    ? 'bg-eliza-red text-white'
                    : 'border border-gray-200 bg-white text-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-600',
                )}>
                  {i + 1}
                </div>
                <span className={cn(
                  'text-center text-[10px] leading-tight',
                  hasRule
                    ? 'font-medium text-charcoal dark:text-gray-200'
                    : 'text-gray-400 dark:text-gray-500',
                )}>
                  {step.label}
                </span>
                {hasRule ? (
                  <span className="text-[9px] font-semibold text-eliza-red/60">gated</span>
                ) : (
                  <span className="text-[9px] text-gray-300 dark:text-gray-600">open</span>
                )}
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Segmented control
// ---------------------------------------------------------------------------

function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-800">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            'rounded-md px-3 py-1 text-xs font-medium transition-[background-color,color,box-shadow] duration-150',
            FOCUS_RING,
            value === opt.value
              ? 'bg-white text-charcoal shadow-sm dark:bg-gray-700 dark:text-gray-100'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Toggle row
// ---------------------------------------------------------------------------

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string
  description?: string
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className={cn(
      'flex cursor-pointer items-start gap-3 rounded-lg p-2.5',
      'transition-colors duration-100 hover:bg-gray-50 dark:hover:bg-white/[0.03]',
      disabled && 'pointer-events-none opacity-40',
    )}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
        className={cn('mt-0.5 h-4 w-4 rounded border-gray-300 accent-eliza-red', FOCUS_RING)}
      />
      <div>
        <p className="text-xs font-medium text-charcoal dark:text-gray-200">{label}</p>
        {description && (
          <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">{description}</p>
        )}
      </div>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Condition row — readable view in card body
// ---------------------------------------------------------------------------

function ConditionRow({
  condition,
  connector,
  steps,
}: {
  condition: CloseCalendarRule['conditions'][0]
  connector?: 'and' | 'or'
  steps: CloseCalendarPipelineStep[]
}) {
  const isApproval = condition.source_type === 'role_approval'
  const subject = isApproval
    ? roleName(condition.source_item_key ?? '')
    : stepLabel(condition.source_item_key ?? '', steps)

  return (
    <div className="space-y-1">
      {connector && (
        <div className="px-3">
          <span className="text-[9px] font-bold uppercase tracking-widest text-gray-300 dark:text-gray-600">
            {connector}
          </span>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 rounded-lg bg-gray-50 px-3 py-2 dark:bg-white/[0.04]">
        <div className="flex min-w-0 items-center gap-2">
          {isApproval && (
            <span className="shrink-0 rounded px-1 py-px text-[8px] font-bold uppercase tracking-wider bg-violet-100 text-violet-500 dark:bg-violet-950/40 dark:text-violet-400">
              role
            </span>
          )}
          <span className="truncate text-xs font-medium text-charcoal dark:text-gray-200">
            {subject}
          </span>
        </div>
        <span className="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
          {isApproval ? 'approves' : 'is complete'}
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Condition builder rows (used in both edit card and create panel)
// ---------------------------------------------------------------------------

function ConditionBuilder({
  conditions,
  conditionLogic,
  targetKey,
  steps,
  onAdd,
  onRemove,
  onUpdateType,
  onUpdateKey,
}: {
  conditions: ConditionDraft[]
  conditionLogic: 'all' | 'any'
  targetKey: string
  steps: CloseCalendarPipelineStep[]
  onAdd: () => void
  onRemove: (id: string) => void
  onUpdateType: (id: string, type: 'applet' | 'approval') => void
  onUpdateKey: (id: string, key: string) => void
}) {
  const usedAppletKeys = new Set(
    conditions.filter(c => c.type === 'applet').map(c => c.key).filter(Boolean)
  )
  const isAll = conditionLogic === 'all'

  return (
    <div className="space-y-1.5">
      {conditions.map((cond, i) => {
        const isApproval = cond.type === 'approval'
        const availableApplets = steps.filter(step =>
          step.key !== targetKey && (!usedAppletKeys.has(step.key) || step.key === cond.key)
        )

        return (
          <div key={cond.id} className="space-y-1">
            {i > 0 && (
              <div className="flex items-center gap-2 pl-1">
                <span className={cn(
                  'rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-widest',
                  isAll
                    ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-amber-50 text-amber-500 dark:bg-amber-950/30 dark:text-amber-400',
                )}>
                  {isAll ? 'AND' : 'OR'}
                </span>
              </div>
            )}
            <div className={cn(
              'flex items-center gap-2 rounded-lg border p-2',
              isApproval
                ? 'border-violet-200/60 bg-violet-50/50 dark:border-violet-800/30 dark:bg-violet-950/10'
                : 'border-gray-200 bg-gray-50/60 dark:border-gray-600/50 dark:bg-white/[0.02]',
            )}>
              {/* Type toggle */}
              <div className="flex shrink-0 rounded-md border border-gray-200 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-800">
                {(['applet', 'approval'] as const).map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onUpdateType(cond.id, t)}
                    className={cn(
                      'rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                      'transition-[background-color,color,box-shadow] duration-100',
                      FOCUS_RING,
                      cond.type === t
                        ? t === 'approval'
                          ? 'bg-violet-600 text-white shadow-sm'
                          : 'bg-eliza-red text-white shadow-sm'
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300',
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <select
                className={FORM_SELECT}
                value={cond.key}
                onChange={e => onUpdateKey(cond.id, e.target.value)}
              >
                <option value="">{isApproval ? 'Choose role…' : 'Choose step…'}</option>
                {isApproval
                  ? APPROVAL_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)
                  : availableApplets.map(step => <option key={step.key} value={step.key}>{step.label}</option>)}
              </select>

              <span className="shrink-0 text-[11px] text-gray-400">
                {isApproval ? 'approves' : 'done'}
              </span>

              <button
                type="button"
                aria-label="Remove"
                onClick={() => onRemove(cond.id)}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-gray-300',
                  'transition-[background-color,color] duration-100',
                  'hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-950/20 dark:hover:text-rose-400',
                  FOCUS_RING,
                )}
              >
                <XMarkIcon className="h-3 w-3" />
              </button>
            </div>
          </div>
        )
      })}

      <button
        type="button"
        onClick={onAdd}
        className={cn(
          'flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-2 text-xs font-medium text-gray-400',
          'transition-[background-color,border-color,color] duration-150',
          'hover:border-eliza-red/30 hover:bg-eliza-red/[0.03] hover:text-eliza-red',
          'dark:border-gray-600 dark:hover:border-eliza-red/30 dark:hover:text-eliza-red',
          FOCUS_RING,
        )}
      >
        <PlusIcon className="h-3 w-3" />
        Add condition
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Rule card
// ---------------------------------------------------------------------------

function RuleCard({
  rules,
  stepNumber,
  steps,
  onSave,
  isSaving,
  onDelete,
  isDeleting,
}: {
  rules: CloseCalendarRule[]
  stepNumber: number
  steps: CloseCalendarPipelineStep[]
  onSave: (ruleId: number, changes: Partial<RuleEditState>) => void
  isSaving: boolean
  onDelete: (ruleIds: number[]) => void
  isDeleting: boolean
}) {
  const rule = rules[0]
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [draft, setDraft] = useState<RuleEditState>({
    enforcement_mode: rule.enforcement_mode as 'hard' | 'soft',
    condition_logic: rule.condition_logic as 'all' | 'any',
    is_active: rule.is_active,
    allow_override: rule.allow_override,
    override_requires_reason: rule.override_requires_reason,
    override_requires_dual_approval: rule.override_requires_dual_approval,
  })

  const conditions = rule.conditions ?? []
  const isHard = rule.enforcement_mode === 'hard'
  const isAll = rule.condition_logic === 'all'
  const appletName = stepLabel(rule.target_item_key, steps)

  // Human-readable list of gated actions
  const actionsList = rules
    .map(r => ACTION_READABLE[r.guarded_action] ?? r.guarded_action)
    .join(' · ')

  function cancelEdit() {
    setDraft({
      enforcement_mode: rule.enforcement_mode as 'hard' | 'soft',
      condition_logic: rule.condition_logic as 'all' | 'any',
      is_active: rule.is_active,
      allow_override: rule.allow_override,
      override_requires_reason: rule.override_requires_reason,
      override_requires_dual_approval: rule.override_requires_dual_approval,
    })
    setEditing(false)
    setConfirmDelete(false)
  }

  return (
    <div className={cn(
      'flex flex-col rounded-xl border bg-white',
      'shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_8px_rgba(0,0,0,0.04)]',
      'transition-shadow duration-150',
      'dark:bg-gray-800/70 dark:shadow-none',
      !rule.is_active
        ? 'border-gray-200/60 opacity-60 dark:border-gray-700/40'
        : 'border-gray-200 dark:border-gray-600/60',
    )}>

      {/* Header */}
      <div className="flex items-start gap-3 p-4 pb-3">
        {/* Step number */}
        <div className={cn(
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums',
          rule.is_active
            ? 'bg-eliza-red/10 text-eliza-red'
            : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500',
        )}>
          {stepNumber}
        </div>

        <div className="min-w-0 flex-1">
          {/* Applet name — the business entity */}
          <p className="text-sm font-semibold leading-tight text-charcoal dark:text-gray-100">
            {appletName}
          </p>

          {/* Actions gated */}
          <p className="mt-0.5 text-[11px] text-gray-400 dark:text-gray-500">
            {isHard ? 'Blocks' : 'Warns on'}{' '}
            <span className="text-gray-500 dark:text-gray-400">{actionsList}</span>
          </p>
        </div>

        {/* Enforcement badge */}
        <div className={cn(
          'mt-0.5 flex shrink-0 items-center gap-1 rounded-md px-2 py-1',
          isHard
            ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400'
            : 'bg-amber-50 text-amber-600 dark:bg-amber-950/20 dark:text-amber-400',
        )}>
          {isHard
            ? <LockClosedIcon className="h-3 w-3" />
            : <ExclamationTriangleIcon className="h-3 w-3" />
          }
          <span className="text-[10px] font-semibold">
            {isHard ? 'Hard' : 'Soft'}
          </span>
        </div>
      </div>

      {/* Body — conditions (read mode) */}
      {!editing && (
        <div className="border-t border-gray-100 px-4 pb-3 pt-3 dark:border-gray-700/60">
          {/* Prerequisites header */}
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              Prerequisites
            </span>
            <span className={cn(
              'rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-wider',
              isAll
                ? 'bg-gray-100 text-gray-400 dark:bg-gray-700/70 dark:text-gray-500'
                : 'bg-amber-50 text-amber-500 dark:bg-amber-950/30 dark:text-amber-400',
            )}>
              {isAll ? 'all of' : 'any of'}
            </span>
          </div>

          {conditions.length > 0 ? (
            <div className="space-y-1">
              {conditions.map((c, i) => (
                <ConditionRow
                  key={c.id}
                  condition={c}
                  steps={steps}
                  connector={i > 0 ? (isAll ? 'and' : 'or') : undefined}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs italic text-gray-400 dark:text-gray-500">No conditions defined</p>
          )}

          {/* Override summary */}
          {(rule.allow_override || !rule.allow_override) && (
            <div className="mt-3 border-t border-gray-100 pt-2.5 dark:border-gray-700/60">
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-400 dark:text-gray-500">
                <span>{rule.allow_override ? 'Override: allowed' : 'Override: not allowed'}</span>
                {rule.allow_override && rule.override_requires_reason && (
                  <span>· requires reason</span>
                )}
                {rule.allow_override && rule.override_requires_dual_approval && (
                  <span>· dual approval required</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit form */}
      {editing && (
        <div className="border-t border-gray-100 p-4 dark:border-gray-700/60">
          <div className="space-y-5">

            {/* Gate type */}
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                Gate type
              </p>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'hard' as const, label: 'Hard block', sub: 'Prevents until conditions pass' },
                  { value: 'soft' as const, label: 'Soft warning', sub: 'Allows with a notice' },
                ]).map(g => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setDraft(d => ({ ...d, enforcement_mode: g.value }))}
                    className={cn(
                      'flex flex-col rounded-lg border p-2.5 text-left text-xs transition-[background-color,border-color] duration-150',
                      FOCUS_RING,
                      draft.enforcement_mode === g.value
                        ? g.value === 'hard'
                          ? 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-950/20'
                          : 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20'
                        : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-transparent dark:hover:bg-white/[0.03]',
                    )}
                  >
                    <span className={cn(
                      'font-semibold',
                      draft.enforcement_mode === g.value
                        ? g.value === 'hard' ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400'
                        : 'text-charcoal dark:text-gray-200',
                    )}>
                      {g.label}
                    </span>
                    <span className="mt-0.5 text-[10px] text-gray-400">{g.sub}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Condition logic */}
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                Condition logic
              </p>
              <SegmentedControl
                options={[
                  { value: 'all', label: 'All must pass' },
                  { value: 'any', label: 'Any must pass' },
                ]}
                value={draft.condition_logic}
                onChange={v => setDraft(d => ({ ...d, condition_logic: v }))}
              />
            </div>

            {/* Options */}
            <div>
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                Options
              </p>
              <div className="space-y-0.5">
                <ToggleRow
                  label="Rule is active"
                  description="Inactive rules are skipped during gate evaluation"
                  checked={draft.is_active}
                  onChange={v => setDraft(d => ({ ...d, is_active: v }))}
                />
                <ToggleRow
                  label="Allow manual override"
                  description="Authorized users can bypass this gate with justification"
                  checked={draft.allow_override}
                  onChange={v => setDraft(d => ({
                    ...d,
                    allow_override: v,
                    override_requires_reason: v ? d.override_requires_reason : false,
                    override_requires_dual_approval: v ? d.override_requires_dual_approval : false,
                  }))}
                />
              </div>

              {/* Override sub-options — only meaningful when override is allowed */}
              <div className={cn(
                'mt-1 rounded-lg border border-gray-100 bg-gray-50/60 dark:border-gray-700/50 dark:bg-white/[0.02]',
                'transition-opacity duration-150',
                !draft.allow_override && 'pointer-events-none opacity-40',
              )}>
                <ToggleRow
                  label="Requires written reason"
                  description="Override submitter must explain why the gate is being bypassed"
                  checked={draft.override_requires_reason}
                  onChange={v => setDraft(d => ({ ...d, override_requires_reason: v }))}
                  disabled={!draft.allow_override}
                />
                <ToggleRow
                  label="Requires dual approval"
                  description="Two authorized users must approve the override"
                  checked={draft.override_requires_dual_approval}
                  onChange={v => setDraft(d => ({ ...d, override_requires_dual_approval: v }))}
                  disabled={!draft.allow_override}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className={cn(
        'mt-auto flex items-center border-t px-4 py-2.5',
        'border-gray-100 dark:border-gray-700/60',
        confirmDelete ? 'justify-between' : 'justify-between',
      )}>
        {confirmDelete ? (
          <>
            <p className="text-xs text-rose-600 dark:text-rose-400">Delete this rule?</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={isDeleting}
                onClick={() => onDelete(rules.map(r => r.id))}
              >
                {isDeleting && <Spinner size="xs" className="mr-1.5" />}
                Delete
              </Button>
            </div>
          </>
        ) : editing ? (
          <>
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className={cn(
                'flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-gray-400',
                'transition-[background-color,color] duration-150',
                'hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/20 dark:hover:text-rose-400',
                FOCUS_RING,
              )}
            >
              <TrashIcon className="h-3 w-3" />
              Delete
            </button>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" onClick={cancelEdit}>Cancel</Button>
              <Button
                size="sm"
                disabled={isSaving}
                onClick={() => { rules.forEach(r => onSave(r.id, draft)); setEditing(false) }}
              >
                {isSaving && <Spinner size="xs" className="mr-1.5" />}
                Save changes
              </Button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className={cn(
              'ml-auto flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-gray-400',
              'transition-[background-color,color] duration-150',
              'hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/[0.06] dark:hover:text-gray-200',
              FOCUS_RING,
            )}
          >
            Edit rule
            <ChevronRightIcon className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Create panel
// ---------------------------------------------------------------------------

function CreateRulePanel({
  templateId,
  steps,
  onClose,
}: {
  templateId: number
  steps: CloseCalendarPipelineStep[]
  onClose: () => void
}) {
  const [targetKey, setTargetKey] = useState('')
  const [enforcementMode, setEnforcementMode] = useState<'hard' | 'soft'>('hard')
  const [conditionLogic, setConditionLogic] = useState<'all' | 'any'>('all')
  const [conditions, setConditions] = useState<ConditionDraft[]>([
    { id: `${Date.now()}`, type: 'applet', key: '' },
  ])

  const createRule = useCreateCloseCalendarRule()

  const isValid =
    Boolean(targetKey) &&
    conditions.length > 0 &&
    conditions.every(c => Boolean(c.key))

  async function handleSubmit() {
    if (!isValid) return
    await createRule.mutateAsync({
      templateId,
      target_item_key: targetKey,
      conditions: conditions.map(c => ({ type: c.type, key: c.key } as ConditionItem)),
      enforcement_mode: enforcementMode,
      condition_logic: conditionLogic,
    })
    onClose()
  }

  return (
    <div className="col-span-full rounded-xl border border-eliza-red/20 bg-white shadow-[0_1px_4px_rgba(177,30,76,0.06)] dark:bg-gray-800/70 dark:border-gray-600/60">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 dark:border-gray-700/60">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-eliza-red" />
          <span className="font-subtitle text-sm font-semibold text-charcoal dark:text-gray-100">
            New gate rule
          </span>
        </div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md text-gray-400',
            'transition-[background-color,color] duration-150 hover:bg-gray-100 hover:text-gray-600',
            'dark:hover:bg-white/10 dark:hover:text-gray-200',
            FOCUS_RING,
          )}
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="grid gap-6 p-5 sm:grid-cols-2">

        {/* Left col */}
        <div className="space-y-5">
          {/* Target step */}
          <div className="space-y-2">
            <label className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              This rule gates
            </label>
            <select
              className={FORM_SELECT}
              value={targetKey}
              onChange={e => {
                setTargetKey(e.target.value)
                setConditions(prev =>
                  prev.map(c => c.type === 'applet' && c.key === e.target.value ? { ...c, key: '' } : c)
                )
              }}
            >
              <option value="">Choose a close step…</option>
              {steps.map(step => (
                <option key={step.key} value={step.key}>{step.label}</option>
              ))}
            </select>
          </div>

          {/* Gate type */}
          <div className="space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              Gate type
            </p>
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'hard' as const, label: 'Hard block', sub: 'Blocks until conditions pass' },
                { value: 'soft' as const, label: 'Soft warning', sub: 'Warns but allows proceeding' },
              ]).map(g => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setEnforcementMode(g.value)}
                  className={cn(
                    'flex flex-col rounded-lg border p-2.5 text-left text-xs transition-[background-color,border-color] duration-150',
                    FOCUS_RING,
                    enforcementMode === g.value
                      ? g.value === 'hard'
                        ? 'border-rose-200 bg-rose-50 dark:border-rose-800/50 dark:bg-rose-950/20'
                        : 'border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/20'
                      : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-transparent dark:hover:bg-white/[0.03]',
                  )}
                >
                  <span className={cn(
                    'font-semibold',
                    enforcementMode === g.value
                      ? g.value === 'hard' ? 'text-rose-700 dark:text-rose-400' : 'text-amber-700 dark:text-amber-400'
                      : 'text-charcoal dark:text-gray-200',
                  )}>
                    {g.label}
                  </span>
                  <span className="mt-0.5 text-[10px] text-gray-400">{g.sub}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right col — conditions */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
              Prerequisites
            </p>
            <SegmentedControl
              options={[
                { value: 'all', label: 'All of' },
                { value: 'any', label: 'Any of' },
              ]}
              value={conditionLogic}
              onChange={setConditionLogic}
            />
          </div>

          <ConditionBuilder
            conditions={conditions}
            conditionLogic={conditionLogic}
            targetKey={targetKey}
            steps={steps}
            onAdd={() => setConditions(prev => [...prev, { id: `${Date.now()}`, type: 'applet', key: '' }])}
            onRemove={id => setConditions(prev => prev.filter(c => c.id !== id))}
            onUpdateType={(id, type) => setConditions(prev => prev.map(c => c.id === id ? { ...c, type, key: '' } : c))}
            onUpdateKey={(id, key) => setConditions(prev => prev.map(c => c.id === id ? { ...c, key } : c))}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3.5 dark:border-gray-700/60">
        {createRule.isError ? (
          <p className="text-xs text-rose-600 dark:text-rose-400">
            {createRule.error?.message ?? 'Failed to create rule.'}
          </p>
        ) : <span />}
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!isValid || createRule.isPending}>
            {createRule.isPending && <Spinner size="xs" className="mr-1.5" />}
            {createRule.isPending ? 'Creating…' : 'Create rule'}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function RulesEngineView({
  template,
  period,
  onSaveRule,
  isSaving,
  onDeleteRule,
  isDeleting,
}: {
  template: CloseCalendarTemplate
  period?: CloseCalendarPeriod
  onSaveRule: (ruleId: number, changes: Partial<RuleEditState>) => void
  isSaving: boolean
  onDeleteRule: (ruleIds: number[]) => void
  isDeleting: boolean
}) {
  const rules = template.rules ?? []
  const [showCreate, setShowCreate] = useState(false)
  const pipelineSteps = buildCloseCalendarPipelineSteps(period?.items ?? [], rules)

  // Group rules that share the same logical gate (same rule_key prefix before `:action`)
  const ruleGroups = Object.values(
    rules.reduce<Record<string, CloseCalendarRule[]>>((acc, rule) => {
      const baseKey = rule.rule_key.includes(':') ? rule.rule_key.split(':')[0] : rule.rule_key
      if (!acc[baseKey]) acc[baseKey] = []
      acc[baseKey].push(rule)
      return acc
    }, {}),
  )

  // Sort groups by pipeline position so cards reflect the close sequence
  const sortedGroups = [...ruleGroups].sort(
    (a, b) => pipelinePosition(a[0].target_item_key, pipelineSteps) - pipelinePosition(b[0].target_item_key, pipelineSteps),
  )

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-subtitle text-sm font-semibold text-charcoal dark:text-gray-100">
            {template.name}
          </p>
          <p className="mt-0.5 tabular-nums text-xs text-gray-400 dark:text-gray-500">
            v{template.version}
            {' · '}
            {sortedGroups.length === 0
              ? 'No gate rules'
              : `${sortedGroups.length} gate ${sortedGroups.length === 1 ? 'rule' : 'rules'}`}
          </p>
        </div>
        {!showCreate && (
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <PlusIcon className="mr-1 h-3.5 w-3.5" />
            New rule
          </Button>
        )}
      </div>

      {/* Pipeline context strip */}
      {(sortedGroups.length > 0 || rules.length === 0) && (
        <PipelineStrip ruleGroups={sortedGroups} steps={pipelineSteps} />
      )}

      {/* Grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {showCreate && (
          <CreateRulePanel
            templateId={template.id}
            steps={pipelineSteps}
            onClose={() => setShowCreate(false)}
          />
        )}

        {sortedGroups.map((group) => (
          <RuleCard
            key={group[0].id}
            rules={group}
            stepNumber={pipelinePosition(group[0].target_item_key, pipelineSteps) + 1}
            steps={pipelineSteps}
            isSaving={isSaving}
            onSave={onSaveRule}
            onDelete={onDeleteRule}
            isDeleting={isDeleting}
          />
        ))}

        {rules.length === 0 && !showCreate && (
          <div className="col-span-full flex flex-col items-center gap-3 rounded-xl border border-dashed border-gray-200 py-16 text-center dark:border-gray-700">
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              No gate rules defined
            </p>
            <p className="max-w-sm text-xs leading-relaxed text-gray-400 dark:text-gray-500">
              Gate rules enforce the close pipeline sequence. Each rule specifies which steps must
              complete before the next one can run.
            </p>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className={cn(
                'mt-1 flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-4 py-2',
                'text-xs font-medium text-gray-400 transition-[background-color,border-color,color] duration-150',
                'hover:border-eliza-red/30 hover:bg-eliza-red/[0.03] hover:text-eliza-red',
                'dark:border-gray-600 dark:hover:border-eliza-red/30 dark:hover:text-eliza-red',
                FOCUS_RING,
              )}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Create first rule
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
